import { Link, router } from '@inertiajs/react'
import { AppShell } from '@/components/shared/AppShell'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Edit2, Globe, EyeOff, Trash2, ExternalLink } from 'lucide-react'

interface BlogPostFull {
  id: number
  title: string
  slug: string
  excerpt: string
  content: string
  cover_image_url?: string
  author_name: string
  category: string
  exam_tags: string[]
  status: string
  published_at?: string
  created_at: string
  created_by: string
  seo_title?: string
  seo_description?: string
  seo_keywords?: string
}

interface Props {
  post: BlogPostFull
}

export default function BlogPostsShow({ post }: Props) {
  const handlePublish = () => {
    if (!window.confirm(`Publish "${post.title}"? It will become publicly visible.`)) return
    router.post(`/admin/blog_posts/${post.id}/publish`)
  }

  const handleUnpublish = () => {
    if (!window.confirm(`Move "${post.title}" back to Draft?`)) return
    router.post(`/admin/blog_posts/${post.id}/unpublish`)
  }

  const handleDelete = () => {
    if (!window.confirm(`Delete "${post.title}" permanently?`)) return
    router.delete(`/admin/blog_posts/${post.id}`)
  }

  return (
    <AppShell>
      <div className="space-y-6 max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-border">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm" className="h-9 w-9 p-0">
              <Link href="/admin/blog_posts"><ArrowLeft className="w-4 h-4" /></Link>
            </Button>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-foreground line-clamp-1">{post.title}</h1>
              <p className="text-xs text-muted-foreground mt-0.5 font-mono">/blog/{post.slug}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {post.status === 'published' && (
              <Button asChild variant="outline" size="sm" className="gap-2">
                <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-3.5 h-3.5" /><span>View Live</span>
                </a>
              </Button>
            )}
            <Button asChild variant="outline" size="sm" className="gap-2">
              <Link href={`/admin/blog_posts/${post.id}/edit`}><Edit2 className="w-3.5 h-3.5" /><span>Edit</span></Link>
            </Button>
            {post.status === 'draft' ? (
              <Button size="sm" className="gap-2" onClick={handlePublish}>
                <Globe className="w-3.5 h-3.5" /><span>Publish</span>
              </Button>
            ) : (
              <Button variant="outline" size="sm" className="gap-2" onClick={handleUnpublish}>
                <EyeOff className="w-3.5 h-3.5" /><span>Unpublish</span>
              </Button>
            )}
            <Button variant="ghost" size="sm" className="gap-2 text-destructive hover:text-destructive" onClick={handleDelete}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Meta */}
        <Card className="border-border bg-card shadow-xs">
          <CardContent className="p-6 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Status</p>
              {post.status === 'published' ? <Badge variant="default">Published</Badge> : <Badge variant="secondary">Draft</Badge>}
            </div>
            <div><p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Category</p><p className="text-foreground">{post.category}</p></div>
            <div><p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Author</p><p className="text-foreground">{post.author_name}</p></div>
            <div><p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Published</p><p className="text-foreground">{post.published_at || '—'}</p></div>
          </CardContent>
        </Card>

        {/* Excerpt */}
        <Card className="border-border bg-card shadow-xs">
          <CardHeader className="px-6 py-4 border-b border-border"><CardTitle className="text-sm font-bold text-foreground">Excerpt</CardTitle></CardHeader>
          <CardContent className="p-6 text-sm text-muted-foreground leading-relaxed">{post.excerpt || '—'}</CardContent>
        </Card>

        {/* Content Preview */}
        <Card className="border-border bg-card shadow-xs">
          <CardHeader className="px-6 py-4 border-b border-border"><CardTitle className="text-sm font-bold text-foreground">Content Preview</CardTitle></CardHeader>
          <CardContent className="p-6">
            <div className="max-h-80 overflow-y-auto text-sm text-foreground leading-relaxed rounded-xl border border-border bg-background p-5" dangerouslySetInnerHTML={{ __html: post.content || '<em>No content</em>' }} />
          </CardContent>
        </Card>
      </div>
    </AppShell>
  )
}
